import {
  OutreachAction,
  OutreachActionType,
  SupplierThread,
  ThreadChange,
  ThreadCommitment,
} from '../components/forms/outreach/types';
import { getCurrencySymbol } from '../utils/currency';

/**
 * Pure model behind the step-4 action surface: analyst actions → thread
 * ordering, change-ribbon groups, and the counter number the buyer approves.
 * No React, no HTTP. Nothing here invents a value — a thread with no analysis
 * produces no read line and no card.
 */

/**
 * Only a term or a fact that needs the buyer's call counts as a decision. A
 * chase on a silent thread stays "waiting" — otherwise every quiet thread
 * eventually shouts, and the server already holds a chase back until the
 * supplier has actually gone quiet. CLOSE_OUT lands on a thread whose quote is
 * in, so it reads under "quote in" rather than as an outstanding decision.
 */
const DECISION_TYPES: OutreachActionType[] = [
  'NEGOTIATE_PRICE',
  'VERIFY_COMPLIANCE',
  'ASK_LEAD_TIME_SPLIT',
  'REQUEST_FORMAL_QUOTE',
  'BUNDLE_ASK',
  'CLARIFY',
];

export const isDecisionAction = (action: OutreachAction): boolean => DECISION_TYPES.includes(action.type);

/**
 * Actions carry the component + supplier they were computed for; a thread's
 * own id changes the first time something is sent (draft-… → comms id), so the
 * component/supplier pair is the stable join and threadId the precise one.
 */
const pairKey = (componentId: string | undefined, supplierName: string): string =>
  `${componentId || ''}::${supplierName || ''}`.toLowerCase();

/**
 * A thread's identity for anything that must survive its first send — the id
 * changes then (draft-… → comms id), so keying on it would read the same
 * conversation as a brand-new row.
 */
export const threadIdentity = (thread: SupplierThread): string => {
  const key = pairKey(thread.componentId, thread.supplierName);
  return key === '::' ? thread.id : key;
};

export interface ActionIndex {
  byThreadId: Map<string, OutreachAction[]>;
  byPair: Map<string, OutreachAction[]>;
}

const push = (map: Map<string, OutreachAction[]>, key: string, action: OutreachAction) => {
  const list = map.get(key);
  if (list) list.push(action);
  else map.set(key, [action]);
};

/**
 * A kickoff action carries no thread id — it is reached through the
 * component + supplier pair, the same join a thread keeps across its first
 * send, so an un-contacted conversation finds its opening asks like any other.
 */
export const buildActionIndex = (actions: OutreachAction[]): ActionIndex => {
  const index: ActionIndex = { byThreadId: new Map(), byPair: new Map() };
  actions
    .filter((a) => a.status === 'OPEN')
    .forEach((action) => {
      if (action.threadId) push(index.byThreadId, action.threadId, action);
      push(index.byPair, pairKey(action.componentId, action.supplierName), action);
    });
  return index;
};

/** Priority 1 acts first; ties keep the server's order (priority, newest). */
const byPriority = (a: OutreachAction, b: OutreachAction) => a.priority - b.priority;

export const actionsForThread = (index: ActionIndex, thread: SupplierThread): OutreachAction[] => {
  const exact = index.byThreadId.get(thread.id);
  const matched = exact && exact.length > 0 ? exact : index.byPair.get(pairKey(thread.componentId, thread.supplierName));
  return matched ? [...matched].sort(byPriority) : [];
};

export const actionForSupplier = (index: ActionIndex, thread: SupplierThread): OutreachAction | undefined =>
  actionsForThread(index, thread)[0];

export type ThreadBucket = 'needs-you' | 'quote-in' | 'waiting' | 'idle';

/**
 * What kind of attention a thread wants. The distinction that matters is
 * doing versus waiting: an un-started supplier with an opening ask ready is
 * something the buyer can send RIGHT NOW, while a thread whose email left
 * yesterday can only be waited on however loudly it is labelled.
 *
 * - `needs-you`  something to send or decide now: an open decision on a live
 *                conversation, or an un-started supplier whose ask is ready.
 * - `quote-in`   sent, quoted, nothing outstanding.
 * - `waiting`    sent, no quote, no live action — the supplier's move.
 * - `idle`       nobody has written to them and there is no ask ready either.
 */
export const bucketOfThread = (thread: SupplierThread, index: ActionIndex): ThreadBucket => {
  const actions = actionsForThread(index, thread);
  if (thread.status === 'not_sent') return actions.length > 0 ? 'needs-you' : 'idle';
  if (actions.some(isDecisionAction)) return 'needs-you';
  if (thread.quote) return 'quote-in';
  return 'waiting';
};

const BUCKET_RANK: Record<ThreadBucket, number> = { 'needs-you': 0, 'quote-in': 1, waiting: 2, idle: 3 };

/** 1 acts first; a thread with nothing open sorts behind every thread that has something. */
const topPriority = (thread: SupplierThread, index: ActionIndex): number => {
  const actions = actionsForThread(index, thread);
  return actions.length > 0 ? actions[0].priority : Number.MAX_SAFE_INTEGER;
};

/**
 * Sorted by what the buyer can do, not by what happened last.
 *
 * The bucket leads: everything actionable sits above everything that is
 * merely waiting, so an un-started supplier with an opening ask ready outranks
 * a thread whose email went out yesterday — which is the whole point of the
 * ordering, and was backwards while the analyst's priority led. Priority then
 * orders within a bucket, and recency breaks the remaining ties. Nothing is
 * hidden; quiet threads sink.
 */
export const sortThreadsByAction = (threads: SupplierThread[], index: ActionIndex): SupplierThread[] =>
  [...threads].sort((a, b) => {
    const rank = BUCKET_RANK[bucketOfThread(a, index)] - BUCKET_RANK[bucketOfThread(b, index)];
    if (rank !== 0) return rank;
    const priority = topPriority(a, index) - topPriority(b, index);
    if (priority !== 0) return priority;
    return new Date(b.lastActivityIso || 0).getTime() - new Date(a.lastActivityIso || 0).getTime();
  });

export const CHANGE_FIELD_LABEL: Record<ThreadChange['field'], string> = {
  QUOTED_PRICE: 'Price',
  LEAD_TIME: 'Lead time',
  MOQ: 'MOQ',
  PAYMENT_TERMS: 'Payment',
  SHIPPING_TERMS: 'Shipping',
};

export interface ChangeRibbonGroup {
  /** The message this ribbon renders under; null when it cannot be placed. */
  afterMessageId: string | null;
  changes: ThreadChange[];
  commitments: ThreadCommitment[];
}

/**
 * Groups the ledger by the reply that caused it, so the ribbon sits where the
 * change happened rather than in a side panel. Entries written before
 * provenance was recorded — or whose message is not in the loaded page — fall
 * into one trailing group at the end of the conversation.
 */
export const changeRibbonGroups = (thread: SupplierThread): ChangeRibbonGroup[] => {
  const known = new Set(thread.messages.map((m) => m.id));
  const groups = new Map<string, ChangeRibbonGroup>();
  const groupFor = (sourceMessageId?: string): ChangeRibbonGroup => {
    const key = sourceMessageId && known.has(sourceMessageId) ? sourceMessageId : '';
    let group = groups.get(key);
    if (!group) {
      group = { afterMessageId: key || null, changes: [], commitments: [] };
      groups.set(key, group);
    }
    return group;
  };

  (thread.changeHistory || []).forEach((change) => groupFor(change.sourceMessageId).changes.push(change));
  // A commitment only joins a ribbon that already has a movement to show —
  // on its own it is a fact, not a change, and the quote strip carries it.
  (thread.commitments || []).forEach((commitment) => {
    const key = commitment.sourceMessageId && known.has(commitment.sourceMessageId) ? commitment.sourceMessageId : '';
    const group = groups.get(key);
    if (group) group.commitments.push(commitment);
  });

  return Array.from(groups.values()).filter((g) => g.changes.length > 0);
};

/** The supplier's own price-validity statement, when they made one. */
export const validityCommitment = (thread: SupplierThread): ThreadCommitment | undefined =>
  (thread.commitments || []).find((c) => c.type === 'VALIDITY');

/**
 * The buyer's number, moved into the analyst's bounds. [floor, ceiling) is
 * half-open: a counter at the supplier's own quote is not a counter, so the
 * ceiling itself is out of range.
 */
export const clampCounter = (
  value: number,
  bounds: { floor?: number | null; ceiling?: number | null },
): { price: number; clamped: boolean } => {
  const { floor, ceiling } = bounds;
  if (ceiling != null && value >= ceiling) {
    const capped = Math.max(floor ?? 0, ceiling - 1);
    return { price: capped, clamped: true };
  }
  if (floor != null && value < floor) return { price: floor, clamped: true };
  return { price: value, clamped: false };
};

const numberPatterns = (price: number): string[] => {
  const plain = String(price);
  const grouped = price.toLocaleString('en-IN');
  const western = price.toLocaleString('en-US');
  return Array.from(new Set([grouped, western, plain]));
};

const escapeRegExp = (value: string): string => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

/**
 * The ask that goes out carries the number the BUYER approved, not
 * the one the analyst proposed. Where the analyst's own figure appears in the
 * sentence it is substituted in place; where it does not, the approved number
 * is stated plainly rather than left implicit.
 */
export const applyCounterToAsk = (
  ask: string,
  anchorPrice: number | null | undefined,
  approvedPrice: number,
  currency: string,
): string => {
  const symbol = getCurrencySymbol(currency);
  const approved = approvedPrice.toLocaleString('en-IN');
  if (anchorPrice != null && anchorPrice !== approvedPrice) {
    let replaced = ask;
    let hit = false;
    numberPatterns(anchorPrice).forEach((pattern) => {
      const regex = new RegExp(escapeRegExp(pattern), 'g');
      if (regex.test(replaced)) {
        hit = true;
        replaced = replaced.replace(regex, approved);
      }
    });
    if (hit) return replaced;
    return `${replaced.trim()} We can commit at ${symbol}${approved} per unit.`;
  }
  return ask;
};

/**
 * Asks as numbered lines in the buyer's own voice — Robbie turns them into a
 * single email, so nothing here pretends to be one. `startAt` continues a list
 * the composer already holds: asks land in the box a few at a time, and
 * restarting at 1 each time would read as a second list rather than more of
 * the same one.
 */
export const numberedAsks = (asks: string[], startAt = 0): string =>
  asks
    .map((ask) => ask.trim())
    .filter(Boolean)
    .map((ask, idx) => `${startAt + idx + 1}. ${ask}`)
    .join('\n');

/** A counter carrying a number: the one move the buyer confirms before staging. */
export const isCounterCard = (action: OutreachAction): boolean =>
  action.type === 'NEGOTIATE_PRICE' && Boolean(action.anchor?.price);

/** A price counter is only a negotiation when there is a quote to counter. */
export const isCounterAsk = (thread: SupplierThread, action: OutreachAction): boolean =>
  action.type === 'NEGOTIATE_PRICE' && Boolean(thread.quote);

/** One card's ask, carrying the buyer's number when they approved one. */
export const askOfAction = (action: OutreachAction, approvedPrice?: number): string =>
  approvedPrice != null
    ? applyCounterToAsk(action.suggestedAsk, action.anchor?.price, approvedPrice, action.anchor?.currency || 'INR')
    : action.suggestedAsk;

/** The asks the composer receives — one card's or several, as numbered lines. */
export const composeAskQuery = (
  thread: SupplierThread,
  actions: OutreachAction[],
  approvedPrice?: number,
  startAt = 0,
): string =>
  numberedAsks(
    actions.map((a) => askOfAction(a, isCounterAsk(thread, a) ? approvedPrice : undefined)),
    startAt,
  );

/** What the buyer saves per the approved counter, on the quantity they need. */
export const counterSaving = (
  approvedPrice: number,
  ceiling: number | null | undefined,
  quantity: number | undefined,
): number | null => {
  if (ceiling == null || !quantity || ceiling <= approvedPrice) return null;
  return (ceiling - approvedPrice) * quantity;
};
